import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { ageInRange } from "../../utils/ageRange";
import { profilePath } from "../../utils/profilePath";
import "../HomeBody/HomeBody.css";

const aws = process.env.REACT_APP_AWS_URL;

function ActorCard({ actor, index }) {
  const { t } = useTranslation();
  const pictureUrl = actor.picture
    ? actor.picture.startsWith("https")
      ? actor.picture
      : `${aws}/${actor.picture}`
    : null;

  if (!pictureUrl) return null;

  const location = [actor.city, actor.country].filter(Boolean).join(", ");

  return (
    <a
      href={profilePath(actor)}
      className="epk-card kk-dark-section tw-group tw-relative tw-block tw-overflow-hidden tw-rounded-xl tw-bg-[#12001f] tw-no-underline tw-border tw-border-white/5 hover:tw-border-[#FF43A7]/40 tw-transition-all tw-duration-300 hover:tw-shadow-[0_8px_30px_rgba(255,67,167,0.18)]"
      style={{ aspectRatio: "2/3", animationDelay: `${index * 0.035}s` }}
    >
      {/* Headshot */}
      <img
        src={pictureUrl}
        alt={`${actor.firstName} ${actor.lastName}`}
        loading="lazy"
        className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
      />

      {/* Gradient scrim */}
      <div
        className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-2/3 tw-pointer-events-none"
        style={{ background: "linear-gradient(to top, rgba(12,0,24,0.97) 0%, rgba(12,0,24,0.65) 50%, transparent 100%)" }}
      />

      {/* Role badge */}
      {actor.role && (
        <span className="tw-absolute tw-top-3 tw-left-3 tw-rounded-full tw-bg-[#FF43A7]/85 tw-px-2.5 tw-py-0.5 tw-text-[9px] tw-font-bold tw-text-white tw-uppercase tw-tracking-wide tw-backdrop-blur-sm">
          {t(actor.role)}
        </span>
      )}

      {/* Rep badge (only meaningful for actors) */}
      {actor.role === "Actor" && actor.hasAgent && (
        <span className="tw-absolute tw-top-3 tw-right-3 tw-rounded-full tw-bg-black/50 tw-px-2 tw-py-0.5 tw-text-[9px] tw-text-[#E2BDC9] tw-backdrop-blur-sm tw-border tw-border-[#5A3F49]/40">
          {t("Rep")}
        </span>
      )}

      {/* Bottom content */}
      <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-p-3">
        <h3 className="tw-text-white tw-font-bold tw-text-sm tw-leading-snug tw-m-0 tw-line-clamp-1">
          {actor.firstName} {actor.lastName}
        </h3>

        {/* Hover reveal */}
        <div className="tw-overflow-hidden tw-max-h-0 group-hover:tw-max-h-24 tw-transition-all tw-duration-300 tw-ease-in-out">
          {location && (
            <p className="tw-text-[#E2BDC9] tw-text-[11px] tw-mt-1.5 tw-m-0 tw-leading-relaxed">
              {location}
            </p>
          )}
          <div className="tw-flex tw-items-center tw-justify-end tw-mt-2">
            <span className="tw-flex tw-items-center tw-gap-1 tw-text-[#FF43A7] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide">
              {t("View Profile")}
              <FontAwesomeIcon icon={faArrowRight} className="tw-text-[8px]" />
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}

// Stored country values each filter option should match: settings saves UK actors by nation,
// and older profiles store the US as "USA".
const COUNTRY_MATCHES = {
  "United Kingdom": ["United Kingdom", "England", "Scotland", "Wales", "Northern Ireland"],
  "United States": ["United States", "USA"],
};

export default function ActorFilterList({ data = [], type = [], emptyLabel }) {
  const { t } = useTranslation();

  const filtered = useMemo(() => {
    if (!type.length) return data;

    return data.filter((actor) =>
      type.every((filter) => {
        if (filter.startsWith("Age Range:")) {
          return ageInRange(actor.age, filter.replace("Age Range:", "").trim());
        }
        if (filter.startsWith("Gender:"))
          return actor.gender === filter.replace("Gender:", "").trim();
        if (filter.startsWith("Ethnicity:"))
          return actor.ethnicity === filter.replace("Ethnicity:", "").trim();
        if (filter.startsWith("Representation:"))
          return actor.hasAgent === (filter.replace("Representation:", "").trim() === "Yes");
        if (filter.startsWith("City:"))
          return actor.city === filter.replace("City:", "").trim();
        if (filter.startsWith("Country:")) {
          const country = filter.replace("Country:", "").trim();
          return (COUNTRY_MATCHES[country] || [country]).includes(actor.country);
        }
        return true;
      })
    );
  }, [data, type]);

  const visible = useMemo(() => filtered.filter((a) => a.picture), [filtered]);

  if (visible.length === 0) {
    return (
      <div className="tw-col-span-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-24 tw-gap-4 tw-text-[#5A3F49]">
        <p className="tw-text-sm tw-m-0">{emptyLabel || t("No actors match your filters.")}</p>
      </div>
    );
  }

  return (
    <>
      {visible.map((actor, index) => (
        <ActorCard key={actor._id} actor={actor} index={index} />
      ))}
    </>
  );
}
